import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { hotels, services } from "../data/data";
import {
  ArrowRight,
  Star,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const Home = () => {
  const { language, t } = useLanguage();
  const featuredHotels = hotels.slice(0, 6);
  const featuredServices = services.slice(0, 3);

  const heroImages = [
    "/images/home/antalya_manavgat.jpg",
    "/images/home/antalya_harbor.jpg",
    "/images/home/antalya_old_town.jpg",
    "/images/home/antalya_waterfall.jpg",
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  };

  const partners = [
    { name: "Akra Hotels", logo: "/images/akra.jpg" },
    { name: "Akra Hotels", logo: "/images/akra.jpg" },
    { name: "Akra Hotels", logo: "/images/akra.jpg" },
    { name: "Akra Hotels", logo: "/images/akra.jpg" },
    { name: "Akra Hotels", logo: "/images/akra.jpg" },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative h-[630px] flex items-center justify-center overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
          >
            <img
              src={image}
              alt={`Antalya ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>
        ))}

        <button
          onClick={prevSlide}
          className="absolute left-4 z-20 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-300"
        >
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 z-20 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-300"
        >
          <ChevronRight className="h-6 w-6 text-white" />
        </button>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? "w-8 bg-amber-400" : "w-2 bg-white/50"
                }`}
            />
          ))}
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-lg animate-fade-in">
            {t("heroTitle")}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
            {t("heroSubtitle")}
          </p>
          <Link
            to="/hotels"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <span>{t("exploreMore")}</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t("featuredHotels")}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-500 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredHotels.map((hotel) => (
              <Link
                key={hotel.id}
                to="/hotels"
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center space-x-1">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span className="font-semibold text-gray-900">
                      {hotel.stars}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {hotel.name}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {hotel.description[language]}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-amber-600 font-semibold hover:text-amber-700 transition-colors flex items-center space-x-1">
                      <span>{t("viewDetails")}</span>
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      < section className="relative py-24 overflow-hidden" >
        <div className="absolute inset-0">
          <img
            src="/images/services/antalya_villa_rental.jpg"
            alt="Luxury Villa"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block px-4 py-1 mb-6 border border-white/30 rounded-full backdrop-blur-sm">
              <span className="text-sm font-medium tracking-wider uppercase">
                {t("privateCollection")}
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 font-serif">
              {t("villaRentalTitle")}
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed font-light">
              {t("villaRentalDesc")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">

              <Link
                to="/contact"
                className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all duration-300 min-w-[200px]"
              >
                <span>{t("contact")}</span>
              </Link>
            </div>
          </div>
        </div>
      </section >

      <section className="py-20 bg-gradient-to-br from-amber-50 via-yellow-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t("ourServices")}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-500 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredServices.map((service) => (
              <Link
                key={service.id}
                to="/services"
                className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name[language]}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {service.name[language]}
                    </h3>
                    <p className="text-white/90 text-sm mb-3">
                      {service.description[language]}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-amber-400">
                        ${service.price}
                      </span>
                      <span className="text-white font-semibold hover:text-amber-400 transition-colors flex items-center space-x-1">
                        <span>{t("viewDetails")}</span>
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <span>{t("viewDetails")}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* References Section */}

      <section className="bg-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            {language === "tr"
              ? "Belgelerimiz"
              : language === "en"
                ? "Documents & Awards"
                : language === "de"
                  ? "Unsere Dokumente"
                  : "Наши документы"}
          </h2>

          <div className="flex justify-center">
            <div
              className="bg-white rounded-2xl shadow-md p-4 max-w-xs cursor-pointer hover:shadow-xl transition-all duration-300"
              onClick={() => setShowModal(true)}
            >
              <img
                src="/images/home/license.jpg"
                alt="License"
                className="w-full h-auto rounded-lg object-contain"
              />
              <p className="mt-3 text-gray-700 font-medium text-sm md:text-base">
                {language === "tr"
                  ? "Marka Tescil Belgemiz"
                  : language === "en"
                    ? "Trademark Registration Certificate"
                    : "License Document"}
              </p>
            </div>
          </div>

          {showModal && (
            <div
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
              onClick={() => setShowModal(false)}
            >
              <div
                className="bg-white rounded-xl p-4 relative max-w-2xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-2 right-2 text-gray-600 hover:text-black text-2xl font-bold"
                >
                  ×
                </button>

                <img
                  src="/images/home/license.jpg"
                  alt="License Full"
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t("getInTouch")}
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {t("heroSubtitle")}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <span>{t("contact")}</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div >
  );
};

export default Home;
